import { FC, memo, useMemo, useState } from "react";
import styled from "styled-components";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Download from "yet-another-react-lightbox/plugins/download";
import orderBy from "lodash.orderby";

import { ImageGenerationProjectResource } from "@/types/resources";
import ImageToolImage from "./ImageToolImage";
import Text from "@/components/Text";
import ShowHideButton from "@/components/ShowHideButton";
import Button from "@/components/Button";
import ImageToolLoadingImage from "./ImageToolLoadingImage";
import { ImageGenerationProjectStatus } from "@/utils/enums";
import ImageToolStatusCount from "./ImageToolStatusCount";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  margin-bottom: 56px;
`;

const BoxContent = styled.div`
  column-count: 3;
  column-gap: 16px;
  column-fill: balance;
  padding: 32px 24px;
  padding-bottom: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const CardTopAcions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Heading = styled(Text)`
  margin-bottom: 4px;
`;

const LoadMoreButton = styled(Button)`
  height: 38px;
  border-radius: 50px;
  padding: 0 16px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
`;

const BoxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
`;

const LoadMoreButtonHolder = styled.div`
  text-align: center;
  padding: 16px 0 32px;
`;

interface Props {
  project: ImageGenerationProjectResource;
  type: "library" | "midjourney" | "instagram" | "getty";
  title: string;
  subtitle: string;
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
}

const ImageToolImages: FC<Props> = memo(
  ({ project, type, title, subtitle, selectedImages, setSelectedImages }) => {
    const initialImagesCount = () => {
      switch (type) {
        case "library":
          return 18;
        case "getty":
          return 18;
        case "instagram":
          return 3;
        default:
          return 100;
      }
    };

    const loadMoreImagesCount = () => {
      switch (type) {
        case "library":
          return 12;
        case "getty":
          return 12;
        case "instagram":
          return 6;
        default:
          return 100;
      }
    };

    const [imagesCount, setimagesCount] = useState(initialImagesCount());

    const loadMoreImages = () => {
      setimagesCount((prev) => prev + loadMoreImagesCount());
    };

    const [showContent, setShowContent] = useState(true);

    const [index, setIndex] = useState(-1);

    const images = useMemo(() => {
      if (type === "instagram") {
        return orderBy(
          project.images[type],
          (img) => img.meta?.likes ?? 0,
          "desc"
        );
      }
      if (type === "midjourney") {
        return orderBy(
          project.images[type],
          (img) => img.meta?.status ?? "success",
          "desc"
        );
      }
      return project.images[type];
    }, [project.images, type]);

    return (
      <Box>
        <Lightbox
          render={{
            iconNext: () => (
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56Z"
                  fill="white"
                  fillOpacity="0.35"
                />
                <path
                  d="M25.5 35.3045L32 28.0001L25.5 20.6958"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
            iconPrev: () => (
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M28 56C12.536 56 0 43.464 0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56Z"
                  fill="white"
                  fillOpacity="0.35"
                />
                <path
                  d="M30.5 35.3045L24 28.0001L30.5 20.6958"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
          }}
          styles={{
            root: {
              "--yarl__color_backdrop": "rgba(0, 0, 0, .25)",
              "--yarl__carousel_padding_px": 40,
            },
          }}
          open={index >= 0}
          close={() => setIndex(-1)}
          slides={images.map((image) => {
            return {
              src: image.versions.original,
              download: `${image.versions.original}?download`,
            };
          })}
          index={index}
          plugins={[Download]}
        />
        <BoxHeader>
          <div>
            <Heading $type="button">{title}</Heading>
            <Text $type="smallHeadline" $color="gray">
              {subtitle}
            </Text>
          </div>
          <CardTopAcions>
            {type === "midjourney" &&
              (!!project.midjourneyFailedMediaCount ||
                !!project.midjourneyFavoritesCount ||
                !!project.midjourneyPendingMediaCount) && (
                <ImageToolStatusCount
                  favoritesCount={project.midjourneyFavoritesCount}
                  pendingCount={project.midjourneyPendingMediaCount}
                  failedCount={project.midjourneyFailedMediaCount}
                />
              )}
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </CardTopAcions>
        </BoxHeader>
        {showContent && (
          <>
            <BoxContent>
              {type !== "midjourney"
                ? images
                    .slice(0, imagesCount)
                    .map((image, index) => (
                      <ImageToolImage
                        type={type}
                        key={image.id}
                        image={image}
                        selectedImages={selectedImages}
                        setSelectedImages={setSelectedImages}
                        onClick={() => setIndex(index)}
                      />
                    ))
                : images
                    .filter((image) => image.meta?.status !== "failed")
                    .map((image, index) => (
                      <ImageToolImage
                        type={type}
                        key={image.id}
                        image={image}
                        selectedImages={selectedImages}
                        setSelectedImages={setSelectedImages}
                        onClick={() => setIndex(index)}
                        aspectRatio={project.aspectRatio}
                      />
                    ))}
              {type === "library" &&
                project.statuses.stocks ===
                  ImageGenerationProjectStatus.PENDING && (
                  <>
                    <ImageToolLoadingImage />
                    <ImageToolLoadingImage />
                    <ImageToolLoadingImage />
                  </>
                )}
              {type === "getty" &&
                project.statuses.getty ===
                  ImageGenerationProjectStatus.PENDING && (
                  <>
                    <ImageToolLoadingImage />
                    <ImageToolLoadingImage />
                    <ImageToolLoadingImage />
                  </>
                )}
              {type === "instagram" &&
                project.statuses.instagram ===
                  ImageGenerationProjectStatus.PENDING && (
                  <ImageToolLoadingImage />
                )}
            </BoxContent>
            {imagesCount < images.length && type !== "midjourney" && (
              <LoadMoreButtonHolder>
                <LoadMoreButton color="secondary" onClick={loadMoreImages}>
                  Load More
                </LoadMoreButton>
              </LoadMoreButtonHolder>
            )}
          </>
        )}
      </Box>
    );
  }
);

export default ImageToolImages;
